/** Bounded music-only metering. Reuses analyser buffers and follows the audible bus. */
/** Ten non-overlapping EQ lanes, from kick/sub to hats/air. These measure the
 * mix, not isolated instrument stems: a note can contribute to several lanes. */
export const MUSIC_EQ_EDGES=[35,75,140,250,440,800,1500,2700,4500,7500,14000] as const;
export interface MusicReactive { level:number; bass:number; mid:number; high:number; beat:number; spectrum:readonly number[] }
export const silentMusic=():MusicReactive=>({level:0,bass:0,mid:0,high:0,beat:0,spectrum:Array(10).fill(0)});
export class MusicReactivity {
  readonly value=silentMusic();
  private wave=new Float32Array(2048);
  private spectrum=new Float32Array(1024);
  sample(analyser:AnalyserNode|null, sampleRate:number, beat:number, gain:number, dt:number):MusicReactive {
    const target=silentMusic(),spectrum=target.spectrum as number[];
    if(analyser && gain>0){
      if(this.wave.length!==analyser.fftSize){this.wave=new Float32Array(analyser.fftSize);this.spectrum=new Float32Array(analyser.frequencyBinCount);}
      analyser.getFloatTimeDomainData(this.wave);analyser.getFloatFrequencyData(this.spectrum);
      let power=0;for(const n of this.wave)power+=n*n;
      target.level=Math.min(1,Math.sqrt(power/this.wave.length)*gain*5);
      const band=(lo:number,hi:number)=>{
        const first=Math.max(1,Math.ceil(lo*analyser.fftSize/sampleRate)),last=Math.min(this.spectrum.length,Math.ceil(hi*analyser.fftSize/sampleRate));
        let energy=0;for(let i=first;i<last;i++)energy+=10**(this.spectrum[i]/10);
        return Math.min(1,Math.sqrt(energy)*gain*3);
      };
      target.bass=band(35,250);target.mid=band(250,2500);target.high=band(2500,12000);
      for(let i=0;i<spectrum.length;i++)spectrum[i]=band(MUSIC_EQ_EDGES[i],MUSIC_EQ_EDGES[i+1]);
      target.beat=target.level*Math.exp(-((beat%1+1)%1)*10);
    }
    const elapsed=Math.max(0,Math.min(.1,dt));
    // Peak now, release afterwards. Attack interpolation moves the visual zenith
    // several frames past the note; the beat envelope must not be low-pass filtered.
    for(const key of ['level','bass','mid','high'] as const){
      this.value[key]=Math.max(target[key],this.value[key]*Math.exp(-elapsed*9));
    }
    this.value.beat=analyser&&gain>0?target.beat:this.value.beat*Math.exp(-elapsed*12);
    const values=this.value.spectrum as number[];
    for(let i=0;i<values.length;i++)values[i]=Math.max(spectrum[i],values[i]*Math.exp(-elapsed*(7+i*.65)));
    return this.value;
  }
}
